import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import LinkIcon from 'ext-ui/svg/icon/LinkIcon'
import React, { KeyboardEvent, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { amexIssuerCodes } from 'src/app/RegisterAccount/RegisterAccount'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import ConfirmCardDetailsModal from 'src/component-modal/ConfirmCardDetailsModal/ConfirmCardDetailsModal'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import DisconnectPhysicalCardModal from 'src/component-modal/DisconnectPhysicalCardModal/DisconnectPhysicalCardModal'
import { Issuers, SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  isActivePhysicalCardStatus,
  isRecoverableEnrollmentFailure,
} from 'src/queries/creditCard.derivations'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import style from './PhysicalCardTransactionsPanel.style'

type PhysicalCardTransactionsPanelProps = {
  className?: string
  creditCard: CreditCardListModel
  canConnect?: boolean
  /** EX-40641 remote-config gate for the confirm-missing-details flow. */
  confirmCardDetailsEnabled?: boolean
  /** EX-40694 remote-config gate for the recoverable-failure retry surfaces. */
  confirmCardDetailsRetryEnabled?: boolean
}

export default function PhysicalCardTransactionsPanel(
  props: PhysicalCardTransactionsPanelProps,
) {
  const {
    className = '',
    creditCard,
    canConnect = true,
    confirmCardDetailsEnabled = true,
    confirmCardDetailsRetryEnabled = true,
  } = props
  const queryClient = useQueryClient()
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false)
  const [showConfirmDetailsModal, setShowConfirmDetailsModal] =
    useState<boolean>(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState<boolean>(false)
  const [isVerifying, setIsVerifying] = useState<boolean>(false)

  const [cardStatus, setCardStatus] = useState<PhysicalCardStatus | null>(
    creditCard.physicalCard.status,
  )
  const [showDisconnectAlertPanel, setShowDisconnectAlertPanel] =
    useState<boolean>(false)

  const { whiteLabelIncludes } = useWhitelabel()
  const translate = useTranslate()
  const dispatch = useDispatch()

  const isAmex = amexIssuerCodes.includes(creditCard.issuer as Issuers)

  const shouldDarkenPanel = whiteLabelIncludes([
    SupportedBrands.Boc,
    SupportedBrands.Pnc,
    SupportedBrands.Bmo,
    SupportedBrands.Corpay,
    SupportedBrands.PacWest,
  ])

  // Flag off = nothing confirmable: the status=null carve-out below and the
  // confirm-details modal routing both collapse to pre-EX-40639 behavior.
  const missingCardDetails = confirmCardDetailsEnabled
    ? (creditCard.physicalCard.missingCardDetails ?? [])
    : []
  // Only a meaningful carve-out when the user can actually act on it — without `canConnect` there
  // is no Connect CTA to route into the confirm-details flow, so falling through would render a
  // dead panel (body text, no action) instead of the pre-existing fully-hidden state.
  const isConfirmableMissingDetails =
    missingCardDetails.length > 0 && canConnect

  // EX-40694: a card whose enrollment failed recoverably (bad CVC / unknown failure) should route
  // the user back into the confirm-details flow instead of hiding the panel. Gated on its own
  // retry flag (a fast-follow kill switch — the parent flag was already live in prod) AND
  // isConfirmableMissingDetails, so it only activates with both flags on AND a backend that
  // populates missingCardDetails — an old backend returns [] here, keeping the panel hidden
  // exactly as today.
  const isRecoverableConfirmable =
    confirmCardDetailsRetryEnabled &&
    isRecoverableEnrollmentFailure(creditCard.physicalCard) &&
    isConfirmableMissingDetails

  if (
    whiteLabelIncludes(SupportedBrands.Jpm) ||
    isAmex ||
    // A null status normally means "not eligible right now" and hides the
    // panel entirely — except when the card is confirmable (missing card
    // details): the API deliberately returns status=null rather than
    // ENROLLMENT_AVAILABLE for those cards (see
    // PhysicalCardEnrollmentEligibilityService.isEnrollable vs.
    // missingEnrollmentDetails), and without this carve-out the panel would
    // never render for the exact population EX-40639 exists to serve.
    (!creditCard.physicalCard.status && !isConfirmableMissingDetails) ||
    // NotApplicable normally hides the panel, but a recoverable enrollment failure with confirmable
    // missing details is exactly the retry path EX-40694 exists to serve — keep the panel mounted
    // so the Connect CTA (which routes to ConfirmCardDetailsModal below) can offer the retry.
    (creditCard.physicalCard.status === PhysicalCardStatus.NotApplicable &&
      !isRecoverableConfirmable) ||
    creditCard.physicalCard.status === PhysicalCardStatus.Closed ||
    creditCard.displayStatus === CreditCardDisplayStatus.Closed
  ) {
    return null
  }

  // For the time being as darkened variants are coming and not in Figma, let's just
  // hide the darkened state and not show the panel at all to keep consistent
  if (shouldDarkenPanel) {
    return null
  }

  const handleConnectClick = () => {
    if (missingCardDetails.length > 0) {
      setShowConfirmDetailsModal(true)
      return
    }
    setShowConnectModal(true)
  }

  const handleDisconnectClick = () => {
    setShowDisconnectModal(true)
  }

  // Translated once per render so it stays a stable primitive dependency below (a fresh
  // `translate` function reference would defeat the useCallback memoization it's used in).
  const successToastMessage = translate('Success! Your card is now active.')

  // Memoized (via stable primitive/setter deps only — never `translate` itself) so
  // PhysicalCardVerifyStep's Pusher-subscription effect doesn't tear down and re-bind on every
  // render of this panel — it depends on `onVerifyComplete` directly. See the identical pattern
  // on `handleVerifyStart`/`handleVerifyError` below and `ConfirmCardDetailsModal`'s
  // `handleVerifyStepError`.
  const handleEnrollmentComplete = useCallback(
    (_cardId: string, physicalCardStatus?: PhysicalCardStatus) => {
      setIsVerifying(false)
      setShowConnectModal(false)
      setShowConfirmDetailsModal(false)
      // EX-40942: the verify step also settles on NEEDS_CVS_VERIFICATION — 3DS passed but the
      // CVS micro-auth (EX code) is still outstanding, and in the failing case the integrator
      // reports ENROLLMENT_ERROR ~2s later. Asserting Active + toasting "now active" here was the
      // false success in the bug report. Only an explicit ACTIVE is a success; an absent or
      // unknown status is NOT assumed active — it just closes the flow and lets the refetched
      // server state (the EX-code prompt, or the failure) render.
      if (isActivePhysicalCardStatus(physicalCardStatus)) {
        setCardStatus(PhysicalCardStatus.Active)
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <p>{successToastMessage}</p>,
          }),
        )
      }
      // Keep AccountSettings's creditCard.physicalCard.status in sync so
      // visibility recomputes on the latest server state.
      queryClient.invalidateQueries({
        queryKey: creditCardsQueryKeys.detail(creditCard.id),
      })
    },
    [dispatch, queryClient, creditCard.id, successToastMessage],
  )

  // Memoized for the same reason as `handleEnrollmentComplete` above — passed through to
  // PhysicalCardVerifyStep (via ReviewEnrollCard/ConfirmCardDetailsModal's own memoized
  // handlers), whose effects re-arm on every identity change of these.
  const handleVerifyStart = useCallback(() => {
    setIsVerifying(true)
  }, [])

  const handleVerifyError = useCallback(() => {
    setIsVerifying(false)
  }, [])

  const isActive = cardStatus === PhysicalCardStatus.Active

  const shouldShowConnectCTA =
    canConnect &&
    (cardStatus === PhysicalCardStatus.Inactive ||
      cardStatus === PhysicalCardStatus.NeedsVerification ||
      cardStatus === PhysicalCardStatus.Pending ||
      cardStatus === PhysicalCardStatus.EnrollmentAvailable ||
      cardStatus === null ||
      // The CTA logic reads the `cardStatus` useState snapshot, which stays NotApplicable for a
      // recoverable-failed card — so patching only the early-return above would render a panel with
      // no CTA. Derive this arm from the prop instead (the recoverable state is only entered across
      // page loads), but drop it once the snapshot goes Active: a successful retry exits the state
      // via handleEnrollmentComplete while the prop stays stale until the invalidated query
      // refetches, and without !isActive the panel shows Connect next to Disconnect. EX-40694.
      (isRecoverableConfirmable && !isActive))

  return (
    <div
      className={cn('PhysicalCardTransactionsPanel', className, {
        darkened: shouldDarkenPanel,
        'has-top-alert-panel': showDisconnectAlertPanel,
      })}
      css={style}
    >
      {showDisconnectAlertPanel && (
        <AlertPanel variant="warning">
          <div className="flex-row gap-4 align-top">
            <AlertTriangleYellowIcon />
            <div className="flex-column gap-2">
              <p className="font-bold">
                {translate('Disconnected Integration')}
              </p>
              <p className="font-regular">
                {translate(
                  'Your physical card transaction feed is disconnected.',
                )}
              </p>
            </div>
          </div>
        </AlertPanel>
      )}
      <ContentCard variant={shouldDarkenPanel ? 'darkened' : 'standard'}>
        <div className="flex-column gap-5">
          <div className="flex-row justify-start gap-2">
            {isActive && <CheckmarkCircleIcon />}
            <p className="font-h3">
              {translate(
                isActive
                  ? 'Physical Card Transactions Enabled'
                  : 'Enable Physical Card Transactions',
              )}
            </p>
          </div>

          <p className="font-regular">
            {translate(
              'All transactions from your credit card will automatically import into Extend for a comprehensive view of card spend.',
            )}
          </p>
        </div>
      </ContentCard>
      <div className="PhysicalCardTransactionsPanel-bottom">
        {shouldShowConnectCTA && (
          <div
            className="linktext-content"
            role="button"
            onClick={handleConnectClick}
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleConnectClick()
              }
            }}
          >
            <div className="flex-row align-center gap-2">
              <p className="font-linktext connect-text">
                {translate('Connect')}
              </p>
              <LinkIcon />
            </div>
          </div>
        )}
        {isActive && (
          <div
            className="linktext-content"
            role="button"
            onClick={handleDisconnectClick}
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleDisconnectClick()
              }
            }}
          >
            <p className="disconnect-text font-linktext">{'Disconnect'}</p>
          </div>
        )}
      </div>
      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => {
            setShowConnectModal(false)
          },
          creditCardId: creditCard.id,
          onVerifyStart: handleVerifyStart,
          onVerifyComplete: handleEnrollmentComplete,
          onVerifyError: handleVerifyError,
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              // Since this is a physical card transaction, we do not need to
              // show acknowledgements for anything but they PHYSICAL_ONLY flow
              eligibility: 'PHYSICAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              // We only have access to the last 4 and not the CVC in this view
              // So I think this might be our best bet in getting this to work
              // The component itself already masks the card number minus the last
              // 4 digits so this should be alright
              number: `**** **** **** ${creditCard.last4}`,
              // We are unable to get CVC info from the card detail view since
              // this data lives in VGS and it may be a nasty security issue
              // to display it here if a user's account is compromised
              cvc: '***',
              expiration: creditCard.expiresFormattedString,
            },
          },
        }}
      />
      <ConfirmCardDetailsModal
        isOpen={showConfirmDetailsModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConfirmDetailsModal(false)
        }}
        creditCardId={creditCard.id}
        last4={creditCard.last4}
        onVerifyStart={handleVerifyStart}
        onVerifyComplete={handleEnrollmentComplete}
        onVerifyError={handleVerifyError}
      />
      <DisconnectPhysicalCardModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false)
        }}
        onDisconnectComplete={() => {
          setShowDisconnectAlertPanel(true)
          setShowDisconnectModal(false)
          setCardStatus(PhysicalCardStatus.Inactive)
          queryClient.invalidateQueries({
            queryKey: creditCardsQueryKeys.detail(creditCard.id),
          })
        }}
        creditCardId={creditCard.id}
      />
    </div>
  )
}
